import React, { useState, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Shield, CheckCircle, XCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';

export default function BackgroundCheck() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/background-check/status');
      setStatus(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setStatus({ status: 'not_submitted' });
      } else {
        setError('Failed to load background check status');
      }
    } finally {
      setLoading(false);
    }
  };

  const submitCheck = async () => {
    if (!consent) {
      setError('You must consent to the background check');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const response = await api.post('/background-check/submit', { consent: true });
      setStatus(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit background check');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case 'approved':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'pending':
      case 'in_progress':
        return <Clock className="h-6 w-6 text-yellow-500" />;
      default:
        return <AlertCircle className="h-6 w-6 text-gray-400" />;
    }
  };

  const getStatusBadge = (statusValue) => {
    const variants = {
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      not_submitted: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      approved: t('background_check_approved', 'Approved'),
      rejected: t('background_check_rejected', 'Rejected'),
      pending: t('background_check_pending', 'Pending Review'),
      in_progress: 'In Progress',
      not_submitted: 'Not Submitted'
    };

    return (
      <Badge className={variants[statusValue] || variants.not_submitted}>
        {labels[statusValue] || statusValue}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-red-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="background-check-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-red-600" />
          <div>
            <CardTitle>{t('background_check', 'Background Check')}</CardTitle>
            <CardDescription>
              Complete your background verification to start accepting rides
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Status Display */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {getStatusIcon(status?.status)}
              <div>
                <div className="font-medium">Current Status</div>
                <div className="text-sm text-gray-500">
                  {status?.submitted_at && `Submitted: ${new Date(status.submitted_at).toLocaleDateString()}`}
                </div>
              </div>
            </div>
            {getStatusBadge(status?.status)}
          </div>

          {/* Notes */}
          {status?.notes && (
            <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
              <strong>Note:</strong> {status.notes}
            </div>
          )}

          {/* Mocked Notice */}
          {status?.mocked && (
            <div className="p-3 bg-yellow-50 text-yellow-800 rounded-lg text-sm border border-yellow-200">
              <strong>MOCKED:</strong> This is a simulated background check. In production, this would integrate with a real background check provider.
            </div>
          )}

          {/* Submit Form (only if not submitted or rejected) */}
          {(status?.status === 'not_submitted' || status?.status === 'rejected') && (
            <div className="space-y-4 pt-4 border-t">
              <div className="text-sm text-gray-600">
                <p className="mb-2">A background check is required before you can accept rides. This includes:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Criminal history check</li>
                  <li>Driving record verification</li>
                  <li>Identity verification</li>
                </ul>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  data-testid="consent-checkbox"
                />
                <label htmlFor="consent" className="text-sm text-gray-600 cursor-pointer">
                  I consent to a background check being performed. I understand that my personal information will be processed securely and in accordance with privacy regulations.
                </label>
              </div>

              <Button
                onClick={submitCheck}
                disabled={!consent || submitting}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="submit-background-check-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  t('submit_background_check', 'Submit Background Check')
                )}
              </Button>
            </div>
          )}

          {/* Completed States */}
          {status?.status === 'approved' && (
            <div className="flex items-center gap-2 p-4 bg-green-50 text-green-800 rounded-lg">
              <CheckCircle className="h-5 w-5" />
              <span>You&apos;re all set! Your background check has been approved.</span>
            </div>
          )}

          {status?.status === 'pending' && (
            <div className="flex items-center gap-2 p-4 bg-yellow-50 text-yellow-800 rounded-lg">
              <Clock className="h-5 w-5" />
              <span>Your background check is being reviewed. This typically takes 1-3 business days.</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
